import { Empty } from 'antd'
import type { TableProps } from 'antd'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import type { Key, ReactNode, Ref, UIEvent } from 'react'

import { buildColumnLayout, isSkimJump, slotRealIndex } from './treadmillMath'
import type { TreadmillLeaf } from './treadmillMath'
import { TreadmillRow } from './TreadmillRow'
import { TreadmillSelectionContext } from './treadmillSelection'
import type { TreadmillSelectionStore } from './treadmillSelection'
import { ROW_HEIGHT, useTreadmillStyles } from './treadmillStyles'
import { useColumnWindow } from './useColumnWindow'

const POINTER_IDLE_MS = 150
const OVERSCAN_ROWS = 4
const FALLBACK_VIEWPORT_HEIGHT = 800

type ScrollBodyRef = Ref<{
  scrollLeft: number
  scrollTo?: (config: { left?: number; top?: number }) => void
}>

export type TreadmillBodyInfo = {
  scrollbarSize: number
  ref: ScrollBodyRef
  onScroll: (info: { currentTarget?: HTMLElement; scrollLeft?: number }) => void
}

export type TreadmillBodyProps<T> = {
  data: readonly T[]
  leaves: Array<TreadmillLeaf<T>>
  totalWidth: number
  rowKey: TableProps<T>['rowKey']
  selectionWidth: number
  selectionStore: TreadmillSelectionStore | null
  rowClassName?: (record: T, index: number) => string | undefined
  onRow?: TableProps<T>['onRow']
  emptyText?: ReactNode
  info: TreadmillBodyInfo
}

export const findScrollParent = (node: HTMLElement): HTMLElement | null => {
  let current = node.parentElement

  while (current) {
    const { overflowY } = getComputedStyle(current)

    if (overflowY === 'auto' || overflowY === 'scroll') {
      return current
    }

    current = current.parentElement
  }

  return null
}

export const readRowKey = <T,>(
  record: T,
  index: number,
  rowKey: TableProps<T>['rowKey']
): Key => {
  if (typeof rowKey === 'function') {
    return rowKey(record, index)
  }

  const property = typeof rowKey === 'string' ? rowKey : 'id'
  const value = (record as Record<string, unknown>)[property]

  return (value as Key) ?? index
}

export const TreadmillBody = <T,>({
  data,
  leaves,
  totalWidth,
  rowKey,
  selectionWidth,
  selectionStore,
  rowClassName,
  onRow,
  emptyText,
  info
}: TreadmillBodyProps<T>) => {
  const { styles } = useTreadmillStyles()
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLDivElement | null>(null)
  const pointerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const layout = useMemo(
    () => buildColumnLayout(leaves, selectionWidth),
    [leaves, selectionWidth]
  )

  const { columnWindow, scheduleColumnWindow } = useColumnWindow(
    scrollerRef,
    layout,
    leaves.length
  )

  const startIndexRef = useRef(0)
  const frameRef = useRef<number | null>(null)
  const [startIndex, setStartIndex] = useState(0)
  const [poolSize, setPoolSize] = useState(0)
  const [isSkimming, setIsSkimming] = useState(false)
  const rowCount = data.length
  const rowHeight = ROW_HEIGHT

  const settleAfterScroll = useCallback(() => {
    const canvas = canvasRef.current

    if (canvas && canvas.style.pointerEvents !== 'none') {
      canvas.style.pointerEvents = 'none'
    }

    if (pointerTimerRef.current) {
      clearTimeout(pointerTimerRef.current)
    }

    pointerTimerRef.current = setTimeout(() => {
      if (canvasRef.current) {
        canvasRef.current.style.pointerEvents = ''
      }

      pointerTimerRef.current = null
      setIsSkimming(false)
    }, POINTER_IDLE_MS)
  }, [])

  useLayoutEffect(() => {
    const scroller = scrollerRef.current

    if (!scroller || !rowCount) {
      return
    }

    const parent = findScrollParent(scroller)

    const updatePoolSize = () => {
      const viewportHeight =
        parent?.clientHeight || window.innerHeight || FALLBACK_VIEWPORT_HEIGHT
      const nextPoolSize = Math.min(
        rowCount,
        Math.ceil(viewportHeight / rowHeight) + OVERSCAN_ROWS * 2
      )

      setPoolSize((currentPoolSize) =>
        currentPoolSize === nextPoolSize ? currentPoolSize : nextPoolSize
      )
    }

    updatePoolSize()

    if (!parent || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updatePoolSize)

      return () => window.removeEventListener('resize', updatePoolSize)
    }

    const resizeObserver = new ResizeObserver(updatePoolSize)
    resizeObserver.observe(parent)

    return () => resizeObserver.disconnect()
  }, [rowCount, rowHeight])

  useEffect(() => {
    const scroller = scrollerRef.current

    if (!scroller || !rowCount || !poolSize) {
      return
    }

    const parent = findScrollParent(scroller)
    const scrollTarget: HTMLElement | Window = parent ?? window
    const readScrollTop = () => (parent ? parent.scrollTop : window.scrollY)

    const readCanvasOffset = () => {
      const canvasTop = scroller.getBoundingClientRect().top

      return parent
        ? canvasTop - parent.getBoundingClientRect().top + parent.scrollTop
        : canvasTop + window.scrollY
    }

    let canvasOffset = readCanvasOffset()

    const processScroll = () => {
      frameRef.current = null

      const rawIndex = Math.floor((readScrollTop() - canvasOffset) / rowHeight)
      const nextStart = Math.max(
        0,
        Math.min(rawIndex - OVERSCAN_ROWS, rowCount - poolSize)
      )

      if (nextStart !== startIndexRef.current) {
        if (isSkimJump(startIndexRef.current, nextStart, poolSize)) {
          setIsSkimming(true)
        }

        startIndexRef.current = nextStart
        setStartIndex(nextStart)
      }

      settleAfterScroll()
    }

    const handleScroll = () => {
      if (frameRef.current === null) {
        frameRef.current = requestAnimationFrame(processScroll)
      }
    }

    const handleResize = () => {
      canvasOffset = readCanvasOffset()
      handleScroll()
    }

    processScroll()
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize)

    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)

      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current)
      }
    }
  }, [settleAfterScroll, rowCount, poolSize, rowHeight])

  useEffect(
    () => () => {
      if (pointerTimerRef.current) {
        clearTimeout(pointerTimerRef.current)
      }
    },
    []
  )

  useImperativeHandle(info.ref, () => scrollerRef.current as HTMLDivElement)

  const handleHorizontalScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      info.onScroll({
        currentTarget: event.currentTarget,
        scrollLeft: event.currentTarget.scrollLeft
      })

      scheduleColumnWindow()
      settleAfterScroll()
    },
    [settleAfterScroll, scheduleColumnWindow, info]
  )

  if (!data.length) {
    return (
      <div className={styles.treadmillEmpty}>
        {emptyText ?? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      </div>
    )
  }

  const effectivePool = poolSize || Math.min(rowCount, 20)

  return (
    <TreadmillSelectionContext value={selectionStore}>
      <div
        ref={scrollerRef}
        className={styles.treadmillScroller}
        onScroll={handleHorizontalScroll}
      >
        <div
          ref={canvasRef}
          className={styles.treadmillCanvas}
          style={{
            width: `var(--twc-total, ${totalWidth}px)`,
            height: rowCount * rowHeight
          }}
        >
          {Array.from({ length: effectivePool }, (_, slot) => {
            const realIndex = slotRealIndex(startIndex, slot, effectivePool)

            if (realIndex >= rowCount) {
              return null
            }

            const record = data[realIndex]
            const rowKeyValue = readRowKey(record, realIndex, rowKey)

            return (
              <TreadmillRow<T>
                key={slot}
                record={record}
                realIndex={realIndex}
                rowKeyValue={rowKeyValue}
                layout={layout}
                columnWindow={columnWindow}
                placeholder={isSkimming}
                rowTop={realIndex * rowHeight}
                rowHeight={rowHeight}
                selectionWidth={selectionWidth}
                hasSelection={Boolean(selectionStore)}
                rowClassName={rowClassName?.(record, realIndex)}
                onRow={onRow}
              />
            )
          })}
        </div>
      </div>
    </TreadmillSelectionContext>
  )
}
